import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DoorOpen, Mail, MessageSquare, MousePointerClick, RefreshCw, Search,
    SlidersHorizontal, UserCheck, UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchLinkClicks, inviteLinkClicker } from '../../communications/api/communicationsApi';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';

/**
 * Who is clicking the two lines at the bottom of every marketing message.
 *
 * Christian Gravias (Monday 13068863308, 17 Sep 2026): "Update analytics where
 * we can see which buyers are clicking Join the off market portal or Update
 * Property Preferences. Please also add next to the analytics of which buyers
 * are clicking join the off market an invite to portal button so we can
 * individually invite anyone thats clicked the link but not signed up."
 *
 * The invite button is the reason the list is worth having. A person who tapped
 * "join the Off Market Portal" and then did not finish signing up is the warmest
 * lead the agency produces, and until now they were invisible - the send went
 * out, somebody clicked, and nothing in the CRM could say who. So the default
 * view of the Off Market tab is the people who clicked and are still NOT
 * members, with the button that fixes that on the row.
 *
 * The numbers start the day the backend began minting a per-recipient link
 * (communications/services/click_tracking.py). Clicks on emails sent before then
 * are not in here and cannot be - the link those people are holding has nobody's
 * name on it - so the panel says so rather than showing a misleading zero.
 */

const NUM = new Intl.NumberFormat('en-AU');

const KINDS = [
    {
        id: 'off_market',
        label: 'Join the Off Market Portal',
        Icon: DoorOpen,
        accent: '#0F172A',
        blurb: 'Tapped "Join the Collings platform" at the bottom of an email or text',
    },
    {
        id: 'preferences',
        label: 'Update Property Preferences',
        Icon: SlidersHorizontal,
        accent: '#0EA5E9',
        blurb: 'Tapped "Update your property preferences" to change what they want to see',
    },
];

// Melbourne time, not the viewer's. A click stamped 11pm in Perth belongs to the
// agency's next day, and "last clicked" is read next to the rest of the CRM's
// dates.
const when = (value) => formatMelbourneShortDate(value) || '—';

const ChannelIcon = ({ channel }) =>
    channel === 'sms'
        ? <MessageSquare className="w-4 h-4 text-purple-500" title="Sent by text" />
        : <Mail className="w-4 h-4 text-blue-500" title="Sent by email" />;

const StatCard = ({ Icon, label, value, sub, accent }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                  style={{ background: `${accent}1a`, color: accent }}>
                <Icon className="w-4 h-4" />
            </span>
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        {sub != null && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

// ── The button the ticket was really about ────────────────────────────────
const InviteButton = ({ row, onInvited }) => {
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(!!row.invite_sent_at || row.invited_recently);

    if (row.has_portal_account) {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                <UserCheck className="w-3.5 h-3.5" /> On the portal
            </span>
        );
    }
    if (sent) {
        return <span className="text-xs text-gray-400">Invite sent</span>;
    }
    if (!row.can_invite) {
        // Almost always "no email address on file" - a text-only contact who
        // tapped the link. Saying so beats a dead button.
        return <span className="text-xs text-gray-400">No email on file</span>;
    }

    const invite = async () => {
        setBusy(true);
        try {
            await inviteLinkClicker(row.id);
            setSent(true);
            toast.success(`Portal invite sent to ${row.name || row.email}`);
            onInvited?.(row);
        } catch (err) {
            toast.error(err?.response?.data?.error || "Couldn't send the invite");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={invite}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold
                       text-white bg-gray-900 hover:bg-gray-700 disabled:opacity-50"
        >
            <UserPlus className="w-3.5 h-3.5" />
            {busy ? 'Sending…' : 'Invite to Portal'}
        </button>
    );
};

export default function LinkClicksPanel() {
    const [kind, setKind] = useState('off_market');
    const [search, setSearch] = useState('');
    // Off by default: the list is about interest, and everyone the link was sent
    // to is just the audience again.
    const [includeUnclicked, setIncludeUnclicked] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        fetchLinkClicks({
            kind,
            clicked: includeUnclicked ? '0' : '1',
            search: search.trim() || undefined,
        })
            .then(setData)
            .catch(() => toast.error("Couldn't load link clicks"))
            .finally(() => setLoading(false));
    }, [kind, includeUnclicked, search]);

    useEffect(() => {
        const timer = setTimeout(load, search ? 300 : 0);
        return () => clearTimeout(timer);
    }, [load, search]);

    const active = KINDS.find((k) => k.id === kind) || KINDS[0];
    const summary = data?.summary?.[kind] || {};
    const rows = useMemo(() => data?.rows || [], [data]);

    return (
        <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                <h3 className="font-bold text-gray-800">Off Market &amp; Preferences link clicks</h3>
                <button type="button" onClick={load}
                        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
                Every email and text carries one of two closing lines. Each one goes out with the
                recipient&apos;s own link, so this is who actually tapped it.
            </p>

            {/* Which of the two lines */}
            <div className="flex flex-wrap gap-2 mb-4">
                {KINDS.map(({ id, label, Icon }) => {
                    const on = id === kind;
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setKind(id)}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border
                                ${on
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                            <span className={on ? 'text-gray-300' : 'text-gray-400'}>
                                {NUM.format(data?.summary?.[id]?.people || 0)}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <StatCard Icon={active.Icon} accent={active.accent}
                          label="People who clicked" value={NUM.format(summary.people || 0)}
                          sub={active.blurb} />
                <StatCard Icon={MousePointerClick} accent="#7C3AED"
                          label="Total clicks" value={NUM.format(summary.clicks || 0)}
                          sub="Repeat taps by the same person included" />
                <StatCard Icon={UserPlus} accent="#059669"
                          label="Clicked but not signed up"
                          value={NUM.format(summary.not_signed_up || 0)}
                          sub={kind === 'off_market'
                              ? 'Invite them below, one at a time'
                              : 'Preferences work without a portal account'} />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, email or mobile"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg
                                   focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                    />
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-gray-500">
                    <input type="checkbox" checked={includeUnclicked}
                           onChange={(e) => setIncludeUnclicked(e.target.checked)} />
                    Include everyone it was sent to
                </label>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                                <th className="px-4 py-2 font-medium">Buyer</th>
                                <th className="px-4 py-2 font-medium">Sent via</th>
                                <th className="px-4 py-2 font-medium text-right">Clicks</th>
                                <th className="px-4 py-2 font-medium">Last clicked</th>
                                <th className="px-4 py-2 font-medium text-right">Portal</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((row) => (
                                <tr key={row.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5">
                                        <div className="font-medium text-gray-800">
                                            {row.name || row.email || row.phone || 'Unnamed'}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {row.email || row.phone || '—'}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5"><ChannelIcon channel={row.channel} /></td>
                                    <td className="px-4 py-2.5 text-right text-gray-700">
                                        {NUM.format(row.click_count)}
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                                        {when(row.last_clicked_at)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        {kind === 'off_market'
                                            ? <InviteButton row={row} onInvited={load} />
                                            : (row.has_portal_account
                                                ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                                      <UserCheck className="w-3.5 h-3.5" /> On the portal
                                                  </span>
                                                : <span className="text-xs text-gray-400">Not a member</span>)}
                                    </td>
                                </tr>
                            ))}
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">
                                        No clicks recorded yet. Tracking starts from the first send
                                        after this went live, so earlier emails are not counted.
                                    </td>
                                </tr>
                            )}
                            {loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">
                                        Loading…
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {data?.truncated && (
                    <p className="text-xs text-gray-400 px-4 py-2 border-t border-gray-100">
                        Showing the 500 most recent. Narrow it with the search box.
                    </p>
                )}
            </div>
        </div>
    );
}
